import type { HsVector3View } from '@sibe/hyperscene';
import type { HsScreenRay } from './measurement-ray.function';
import {
  hsResolveMeasurementPoint,
  type HsMeasurementHit,
  type HsMeasurementPlacement,
  type HsMeasurementPointSource,
  type HsMeasurementSnapMode,
} from './measurement-snap.function';
import {
  hsConvertLinearMeasurement,
  hsFormatMeasurement,
  hsMeasure,
  hsMeasureDeltas,
  hsMeasurementTargetPoints,
  type HsMeasurementDeltas,
  type HsMeasurementMode,
  type HsMeasurementPoint,
  type HsMeasurementUnit,
} from './measurement.function';

/** What adding a point did. */
export type HsMeasurementStep = 'added' | 'completed' | 'restarted';

/**
 * The points of one measurement and what they measure: a distance (2 points), an angle (3), a polyline or an
 * area (any number, finished by the host). Places clicks by its snap mode and draws as overlay lines and points;
 * storing finished measurements is the host's job.
 */
export class HsMeasurementTool {
  public mode: HsMeasurementMode = 'distance';
  public unit: HsMeasurementUnit = 'm';
  public snapMode: HsMeasurementSnapMode = 'auto';
  /**
   * Real meters per world unit of the measured model, 1 unless calibrated (`hsCalibrationScale`): photogrammetry
   * keeps the units of its source, which are not always meters.
   */
  public scale: number = 1;
  private pointsValue: HsMeasurementPoint[] = [];
  private sourcesValue: HsMeasurementPointSource[] = [];

  public get points(): readonly HsMeasurementPoint[] {
    return this.pointsValue;
  }

  /** How each point was placed, in point order. */
  public get sources(): readonly HsMeasurementPointSource[] {
    return this.sourcesValue;
  }

  /** `snap` when every point lies on a drawn surface, `plane` when one was placed on the level. */
  public get source(): HsMeasurementPointSource {
    return this.sourcesValue.includes('plane') ? 'plane' : 'snap';
  }

  /** Whether the next point starts a new measurement: none yet, or a fixed-count mode is complete. */
  public get startsOver(): boolean {
    const target: number = this.targetPoints;
    return this.pointsValue.length === 0 || (target > 0 && this.pointsValue.length >= target);
  }

  /** Height of the level that points off the model land on: the first point's, null when the next one starts over. */
  public get level(): number | null {
    return this.startsOver ? null : this.pointsValue[0][1];
  }

  /**
   * Places a click: `hit` is the drawn surface under the cursor, `ray` the camera ray through it. Returns what
   * adding the point did, or null when the snap mode places no point there.
   */
  public place(
    hit: HsMeasurementHit | null,
    ray: HsScreenRay | null,
    selectedNodeId: string | null = null
  ): HsMeasurementStep | null {
    const placement: HsMeasurementPlacement | null = hsResolveMeasurementPoint(this.snapMode, hit, ray, {
      level: this.level,
      selectedNodeId,
    });
    return placement === null ? null : this.addPoint(placement.point, placement.source);
  }

  /** Points the mode completes at on its own; 0 when the host finishes it. */
  public get targetPoints(): number {
    return hsMeasurementTargetPoints(this.mode);
  }

  /**
   * Adds a point. A mode with a fixed point count starts over with this point once it was complete, so
   * clicking on measures again.
   */
  public addPoint(point: HsMeasurementPoint, source: HsMeasurementPointSource = 'snap'): HsMeasurementStep {
    const target: number = this.targetPoints;
    let step: HsMeasurementStep = 'added';
    if (target > 0 && this.pointsValue.length >= target) {
      this.clear();
      step = 'restarted';
    }
    this.pointsValue.push([point[0], point[1], point[2]]);
    this.sourcesValue.push(source);
    return target > 0 && this.pointsValue.length === target ? 'completed' : step;
  }

  /** Replaces the points, all placed by `source`, as when a saved measurement is shown again. */
  public setPoints(points: readonly HsMeasurementPoint[], source: HsMeasurementPointSource = 'snap'): void {
    this.pointsValue = points.map((point: HsMeasurementPoint) => [point[0], point[1], point[2]]);
    this.sourcesValue = points.map(() => source);
  }

  public clear(): void {
    this.pointsValue = [];
    this.sourcesValue = [];
  }

  /** Whether enough points are set to finish: two for lines and distances, three for angles and areas. */
  public get canFinish(): boolean {
    return this.value !== null;
  }

  /**
   * The measured value in meters, square meters or degrees, calibrated by `scale`, or null with too few points. A
   * point measures its elevation in world units.
   */
  public get value(): number | null {
    const raw: number | null = this.rawValue;
    if (raw === null || this.mode === 'angle' || this.mode === 'point') {
      return raw;
    }
    return raw * (this.mode === 'area' ? this.scale * this.scale : this.scale);
  }

  /** The value in world units, before calibration; what a calibration compares with a known length. */
  public get rawValue(): number | null {
    return hsMeasure(this.mode, this.pointsValue.map(toView));
  }

  /** The value with its unit, or `n/a`; a point shows its coordinates. */
  public get label(): string {
    const value: number | null = this.value;
    if (value === null) {
      return 'n/a';
    }
    if (this.mode === 'point') {
      const point: HsMeasurementPoint = this.pointsValue[0];
      const format = (coordinate: number): string => hsConvertLinearMeasurement(coordinate, this.unit).toFixed(3);
      return `X ${format(point[0])}  Y ${format(point[1])}  Z ${format(point[2])} ${this.unit}`;
    }
    return hsFormatMeasurement(this.mode, value, this.unit);
  }

  /** A distance split along the axes, across the ground and up, calibrated by `scale`; null for other modes. */
  public get deltas(): HsMeasurementDeltas | null {
    if (this.mode !== 'distance' || this.pointsValue.length < 2) {
      return null;
    }
    const deltas: HsMeasurementDeltas = hsMeasureDeltas(toView(this.pointsValue[0]), toView(this.pointsValue[1]));
    return {
      dx: deltas.dx * this.scale,
      dy: deltas.dy * this.scale,
      dz: deltas.dz * this.scale,
      horizontal: deltas.horizontal * this.scale,
      vertical: deltas.vertical * this.scale,
    };
  }

  /** Where a value label belongs: the middle of a distance or path, the vertex of an angle, an area's center. */
  public get labelAnchor(): HsMeasurementPoint | null {
    const points: readonly HsMeasurementPoint[] = this.pointsValue;
    if (this.value === null || points.length === 0) {
      return null;
    }
    if (this.mode === 'angle') {
      return points[1];
    }
    if (this.mode === 'distance' || this.mode === 'polyline') {
      const a: HsMeasurementPoint = points[points.length - 2] ?? points[0];
      const b: HsMeasurementPoint = points[points.length - 1];
      return [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2, (a[2] + b[2]) / 2];
    }
    const sum: number[] = points.reduce(
      (total: number[], point: HsMeasurementPoint) => [total[0] + point[0], total[1] + point[1], total[2] + point[2]],
      [0, 0, 0]
    );
    return [sum[0] / points.length, sum[1] / points.length, sum[2] / points.length];
  }

  /** Line segments (pairs of points, flat xyz) that draw the measurement; a distance gets end and middle ticks. */
  public lineVertices(): number[] {
    const points: readonly HsMeasurementPoint[] = this.pointsValue;
    if (points.length < 2) {
      return [];
    }
    if (this.mode === 'distance') {
      return distanceVertices(points[0], points[1]);
    }
    if (this.mode === 'angle') {
      // The two arms meet at the second point.
      return points.length < 3
        ? [...points[0], ...points[1]]
        : [...points[1], ...points[0], ...points[1], ...points[2]];
    }
    const vertices: number[] = [];
    for (let index: number = 1; index < points.length; index++) {
      vertices.push(...points[index - 1], ...points[index]);
    }
    if (this.mode === 'area' && points.length >= 3) {
      vertices.push(...points[points.length - 1], ...points[0]);
    }
    return vertices;
  }

  /** The points, flat xyz. */
  public pointVertices(): number[] {
    return this.pointsValue.flatMap((point: HsMeasurementPoint) => [...point]);
  }

  /** Point colors (flat rgb): the first point red, the second cyan, the rest amber. */
  public pointColors(): number[] {
    return this.pointsValue.flatMap((_point: HsMeasurementPoint, index: number) =>
      index === 0 ? [1, 0.25, 0.12] : index === 1 ? [0.1, 0.85, 1] : [1, 0.72, 0.12]
    );
  }
}

function toView(point: HsMeasurementPoint): HsVector3View {
  return { x: point[0], y: point[1], z: point[2] };
}

function distanceVertices(a: HsMeasurementPoint, b: HsMeasurementPoint): number[] {
  const vertices: number[] = [...a, ...b];
  const direction: number[] = [b[0] - a[0], b[1] - a[1], b[2] - a[2]];
  const length: number = Math.hypot(direction[0], direction[1], direction[2]);
  if (length < 0.000001) {
    return vertices;
  }
  const unit: number[] = direction.map((value: number) => value / length);
  // Ticks lie across the line: one perpendicular for the ends, a second one for the middle.
  const reference: number[] = Math.abs(unit[0]) < 0.8 ? [1, 0, 0] : [0, 1, 0];
  const across: number[] = normalize(cross(unit, reference));
  const acrossMiddle: number[] = normalize(cross(unit, across));
  const tick: number = Math.max(length * 0.035, 0.05);
  const endTick: number = tick * 1.35;
  const middle: number[] = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2, (a[2] + b[2]) / 2];
  for (const [center, axis, size] of [
    [a, across, endTick],
    [b, across, endTick],
    [middle, acrossMiddle, tick],
  ] as const) {
    vertices.push(
      center[0] - axis[0] * size,
      center[1] - axis[1] * size,
      center[2] - axis[2] * size,
      center[0] + axis[0] * size,
      center[1] + axis[1] * size,
      center[2] + axis[2] * size
    );
  }
  return vertices;
}

function cross(a: readonly number[], b: readonly number[]): number[] {
  return [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
}

function normalize(vector: readonly number[]): number[] {
  const length: number = Math.hypot(vector[0], vector[1], vector[2]);
  return length === 0 ? [0, 0, 0] : vector.map((value: number) => value / length);
}
